/**
 * KVORE — Theme Core
 */

'use strict';

window.KVORE = window.KVORE || {};

const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ─────────────────────────────────────────
   NAVIGATION — scroll behaviour
───────────────────────────────────────── */
function initNav() {
  const nav = document.getElementById('SiteNav');
  if (!nav) return;

  const scrollThreshold = 50;
  let ticking = false;

  function updateNav() {
    if (window.scrollY > scrollThreshold) {
      nav.classList.add('site-nav--scrolled');
      nav.classList.remove('site-nav--transparent');
    } else {
      nav.classList.remove('site-nav--scrolled');
      nav.classList.add('site-nav--transparent');
    }
    ticking = false;
  }

  window.addEventListener('scroll', () => {
    if (!ticking) {
      requestAnimationFrame(updateNav);
      ticking = true;
    }
  }, { passive: true });

  updateNav();
}

/* ─────────────────────────────────────────
   MOBILE MENU
───────────────────────────────────────── */
function initMobileMenu() {
  const trigger = document.getElementById('MobileMenuToggle');
  const menu = document.getElementById('MobileMenu');
  if (!trigger || !menu) return;

  trigger.addEventListener('click', () => {
    const isOpen = menu.classList.contains('is-open');
    menu.classList.toggle('is-open');
    menu.setAttribute('aria-hidden', String(isOpen)); // false when opening
    trigger.classList.toggle('is-active');
    trigger.setAttribute('aria-expanded', String(!isOpen));
    toggleBackdrop(!isOpen);
    if (!isOpen) { lockScroll(); } else { unlockScroll(); }
  });
}

/* ─────────────────────────────────────────
   CART DRAWER
───────────────────────────────────────── */
function initCartDrawer() {
  // Checkout — bypass any JS interference, always navigate to /checkout
  document.body.addEventListener('click', (e) => {
    if (e.target.closest('[data-checkout-btn]')) {
      window.location.href = '/checkout';
    }
  }, true); // capture phase so it fires before any bubbling handlers

  // Open drawer via delegation
  document.body.addEventListener('click', (e) => {
    const openBtn = e.target.closest('[data-cart-open]');
    if (openBtn) {
      e.preventDefault();
      const drawer = document.getElementById('CartDrawer');
      if (drawer) openDrawer(drawer);
    }
  });

  // Close drawer via delegation (close button or "continue shopping" link)
  document.body.addEventListener('click', (e) => {
    const closeBtn = e.target.closest('#CartDrawerClose') || e.target.closest('[data-cart-close]');
    if (closeBtn) {
      const drawer = document.getElementById('CartDrawer');
      if (drawer && drawer.classList.contains('is-open')) {
        e.preventDefault();
        closeDrawer(drawer);
      }
    }
  });
}

/* ─────────────────────────────────────────
   CART AJAX — qty +/- and remove
   Single round-trip via Section Rendering:
   the change response carries the freshly
   rendered drawer HTML.
─────────────────────────────────────────── */
function initCartControls() {
  const body = document.getElementById('CartDrawerBody');
  if (!body) return;

  body.addEventListener('click', (e) => {
    const minusBtn = e.target.closest('[data-qty-minus]');
    const plusBtn  = e.target.closest('[data-qty-plus]');
    const removeBtn = e.target.closest('[data-remove]');
    if (!minusBtn && !plusBtn && !removeBtn) return;

    const btn = minusBtn || plusBtn || removeBtn;
    const key = btn.dataset.key;
    const row = btn.closest('.cart-item');

    // Ignore repeat taps on a row that's already mid-request (prevents races
    // where a fast double-tap fires two conflicting quantity changes).
    if (row && row.dataset.busy === '1') return;

    const qtyEl = (btn.closest('.cart-item__qty') || row)?.querySelector('.cart-item__qty-value');
    const currentQty = parseInt(qtyEl?.textContent || '1', 10);

    let newQty;
    if (removeBtn) newQty = 0;
    else if (minusBtn) newQty = Math.max(0, currentQty - 1);
    else newQty = currentQty + 1;

    /* ── OPTIMISTIC UI ──
       Respond to the tap instantly instead of waiting ~1–3s for Shopify's
       /cart/change.js + section render round-trip. The row collapses (or the
       number updates) immediately; the network request then reconciles the
       real totals when it lands. This is what makes removal feel instant. */
    if (row) row.dataset.busy = '1';

    if (newQty === 0 && row) {
      collapseCartRow(row);          // instant fade + height collapse
    } else if (qtyEl) {
      qtyEl.textContent = newQty;    // instant quantity bump
    }

    // Optimistic header badge — reconciled by the server response below.
    const countEl = document.querySelector('[data-cart-count]');
    const currentCount = parseInt(countEl?.textContent || '0', 10);
    let optimisticCount;
    if (removeBtn) optimisticCount = Math.max(0, currentCount - currentQty);
    else if (minusBtn) optimisticCount = Math.max(0, currentCount - 1);
    else optimisticCount = currentCount + 1;
    updateCartCount(optimisticCount);

    // Serialise the write through the queue so rapid removals can't race
    // Shopify's cart or thrash the drawer render (see queueCartMutation).
    queueCartMutation(key, newQty);
  });
}

/* ─────────────────────────────────────────
   CART MUTATION QUEUE
   Cart writes are SERIALISED — one /cart/change.js in flight at a time. Firing
   several at once (e.g. removing 5 items fast) makes Shopify's cart, a single
   serialised resource, race; and each response returns a FULL drawer render that
   fights the others — items flicker/reappear and the drawer thrashes. The fix:
   the optimistic UI responds to every tap instantly (row collapses immediately),
   the change requests run one after another, and the heavy drawer re-render runs
   just ONCE — after the whole rapid batch drains, and only for the latest change.
   Result: rapid removals feel instant and settle correctly, no duplicate work.
───────────────────────────────────────── */
let _cartQueue = Promise.resolve();
let _cartPending = 0;
let _cartRenderToken = 0;

function queueCartMutation(key, quantity) {
  _cartPending++;
  const token = ++_cartRenderToken;

  _cartQueue = _cartQueue
    .then(function () {
      return fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity, sections: 'cart-drawer' }),
      });
    })
    .then(function (r) { return r.json(); })
    .then(function (cart) {
      _cartPending--;
      // Render ONCE, after the batch drains, and only for the latest change —
      // no mid-batch thrash, no stale overwrite. The badge reconciles here too
      // (the optimistic badge already shows the right final number).
      if (_cartPending === 0 && token === _cartRenderToken) {
        updateCartCount(cart.item_count);
        if (cart.sections && cart.sections['cart-drawer']) {
          renderCartSection(cart.sections['cart-drawer']);
        } else {
          return refreshCartDrawer();
        }
      }
    })
    .catch(function (err) {
      _cartPending = Math.max(0, _cartPending - 1);
      console.warn('KVORE: Cart update failed', err);
      // Reconcile from source of truth once the batch settles.
      if (_cartPending === 0) return refreshCartDrawer();
    });

  return _cartQueue;
}

/* Collapse a cart row smoothly (used for optimistic removal). Fixes the height
   first so the transition has something to animate from. */
function collapseCartRow(row) {
  const h = row.offsetHeight;
  row.style.height = h + 'px';
  row.style.overflow = 'hidden';
  void row.offsetHeight; // force reflow so the start height is committed
  row.style.transition =
    'height 0.22s var(--ease-out), opacity 0.18s var(--ease-out), ' +
    'padding 0.22s var(--ease-out), margin 0.22s var(--ease-out)';
  row.style.height = '0';
  row.style.opacity = '0';
  row.style.paddingTop = '0';
  row.style.paddingBottom = '0';
  row.style.pointerEvents = 'none';
}

function updateCartCount(count) {
  document.querySelectorAll('[data-cart-count]').forEach(el => {
    el.textContent = count;
    el.style.display = count > 0 ? '' : 'none';
  });
  document.querySelectorAll('[data-cart-item-count]').forEach(el => {
    el.textContent = count > 0 ? `(${count})` : '';
  });
}

/* Swap the drawer's body/footer/shipping bar from freshly rendered
   section HTML — no extra network requests. */
function renderCartSection(html) {
  const body = document.getElementById('CartDrawerBody');
  if (!body || !html) return;

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const newBody     = doc.getElementById('CartDrawerBody');
  const newFooter   = doc.querySelector('.cart-drawer__footer');
  const newShipping = doc.querySelector('[data-shipping-bar]');

  if (newBody) body.innerHTML = newBody.innerHTML;

  // Update shipping progress bar.
  // NOTE: these lookups MUST be scoped to #CartDrawer. The Moodboard/Wishlist
  // drawer reuses the shared `.cart-drawer__footer` class (its "Shop All"
  // footer, #WishlistFooter). On an EMPTY cart the cart drawer has no footer
  // yet, so an unscoped `document.querySelector('.cart-drawer__footer')` matched
  // the WISHLIST footer and dumped the checkout button into the hidden wishlist
  // drawer — so the checkout button never appeared in the cart on the first add
  // (only a full page refresh, which server-renders the cart footer, fixed it).
  const shippingBar = document.querySelector('#CartDrawer [data-shipping-bar]');
  if (shippingBar && newShipping) {
    shippingBar.innerHTML = newShipping.innerHTML;
  } else if (!shippingBar && newShipping) {
    body.insertAdjacentHTML('beforebegin', newShipping.outerHTML);
  }

  const footer = document.querySelector('#CartDrawer .cart-drawer__footer');
  if (footer && newFooter) {
    footer.innerHTML = newFooter.innerHTML;
  } else if (!footer && newFooter) {
    body.insertAdjacentHTML('afterend', newFooter.outerHTML);
  } else if (footer && !newFooter) {
    footer.remove();
  }
}

function refreshCartDrawer() {
  const body = document.getElementById('CartDrawerBody');
  if (!body) return Promise.resolve();

  // Returns a promise so callers can wait for the drawer to be up to date
  // BEFORE opening it (never open an empty/stale drawer).
  return fetch('/cart?sections=cart-drawer')
    .then(r => r.json())
    .then(sections => {
      if (!sections['cart-drawer']) return;
      renderCartSection(sections['cart-drawer']);
    })
    .catch(() => {
      return fetch('/cart.js')
        .then(r => r.json())
        .then(cart => updateCartCount(cart.item_count));
    });
}

/* ─────────────────────────────────────────
   RENDER CART + OPEN — single source of truth for the add flow order.
   Sequence guaranteed: render the returned cart-drawer section → update the
   badge → ONLY THEN animate the drawer open. So the drawer never opens before
   its content is ready (no empty drawer, no stale data, no loading flicker, no
   late badge). Shared by PDP add-to-cart, product-card Quick Add, and the
   wishlist "Move to Bag" — one implementation, no duplication.
───────────────────────────────────────── */
function renderCartAndOpen(item) {
  const drawer = document.getElementById('CartDrawer');
  const open = () => { if (drawer) openDrawer(drawer); };

  if (item && item.sections && item.sections['cart-drawer']) {
    renderCartSection(item.sections['cart-drawer']);
    const tmp = document.createElement('div');
    tmp.innerHTML = item.sections['cart-drawer'];
    const c = tmp.querySelector('[data-cart-item-count]');
    const m = c && c.textContent.match(/\d+/);
    if (m) updateCartCount(parseInt(m[0], 10));
    open();                       // content is already in the DOM → safe to open
  } else {
    refreshCartDrawer().then(open); // fetch fresh, THEN open — never early
  }
}
window.KVORE.renderCartAndOpen = renderCartAndOpen;

/* ─────────────────────────────────────────
   WISHLIST DRAWER
───────────────────────────────────────── */
function initWishlistDrawer() {
  // Open drawer via delegation
  document.body.addEventListener('click', (e) => {
    const openBtn = e.target.closest('[data-wishlist-open]');
    if (openBtn) {
      e.preventDefault();
      const drawer = document.getElementById('WishlistDrawer');
      if (drawer) openDrawer(drawer);
    }
  });

  // Close drawer via delegation
  document.body.addEventListener('click', (e) => {
    const closeBtn = e.target.closest('#WishlistDrawerClose') || e.target.closest('[data-wishlist-close]');
    if (closeBtn) {
      const drawer = document.getElementById('WishlistDrawer');
      if (drawer && drawer.classList.contains('is-open')) {
        e.preventDefault();
        closeDrawer(drawer);
      }
    }
  });
}

/* ─────────────────────────────────────────
   SCROLL LOCK — iOS-proof body freeze
   position:fixed lock preserves scroll pos.
───────────────────────────────────────── */
let _scrollLockY = 0;
let _scrollLocked = false;

function lockScroll() {
  if (_scrollLocked) return;
  _scrollLockY = window.scrollY;
  document.body.style.position = 'fixed';
  document.body.style.top = `-${_scrollLockY}px`;
  document.body.style.left = '0';
  document.body.style.right = '0';
  document.body.style.overflow = 'hidden';
  _scrollLocked = true;
}

function unlockScroll() {
  if (!_scrollLocked) return;
  document.body.style.position = '';
  document.body.style.top = '';
  document.body.style.left = '';
  document.body.style.right = '';
  document.body.style.overflow = '';
  document.body.style.paddingRight = '';
  /* Restore the scroll INSTANTLY. A bare scrollTo(0, y) honours CSS
     `scroll-behavior`, and the homepage sets `scroll-behavior:smooth` — so on
     drawer close the page snapped to the top then SMOOTH-scrolled back down (the
     visible "page jumps up and down"). `behavior:'instant'` restores in the same
     frame → zero movement. */
  window.scrollTo({ top: _scrollLockY, left: 0, behavior: 'instant' });
  _scrollLocked = false;
}

window.KVORE.lockScroll = lockScroll;
window.KVORE.unlockScroll = unlockScroll;
window.KVORE.resetScrollLockY = function() { _scrollLockY = 0; };

/* ─────────────────────────────────────────
   DRAWER UTILITIES
   Drawers are mutually exclusive — opening
   one closes any other open drawer first.
───────────────────────────────────────── */
/* ─────────────────────────────────────────
   GLOBAL OVERLAY EXCLUSION
   Cart / Wishlist / Search / Mobile menu:
   only ONE may ever occupy the viewport.
   Others snap shut instantly (no transition)
   before the new one renders.
───────────────────────────────────────── */
function _snapShut(el) {
  if (!el || !el.classList.contains('is-open')) return;
  el.style.transition = 'none';           // kill the slide animation
  el.classList.remove('is-open');
  el.setAttribute('aria-hidden', 'true');
  void el.offsetWidth;                    // force reflow so 'none' applies
  requestAnimationFrame(() => { el.style.transition = ''; });
}

function closeAllOverlays(except) {
  ['CartDrawer', 'WishlistDrawer', 'SearchDrawer', 'MobileMenu'].forEach(id => {
    const el = document.getElementById(id);
    if (!el || el === except) return;
    _snapShut(el);
    if (id === 'MobileMenu') {
      const trig = document.getElementById('MobileMenuToggle');
      trig?.classList.remove('is-active');
      trig?.setAttribute('aria-expanded', 'false');
    }
  });
}

window.KVORE.closeAllOverlays = closeAllOverlays;

/* ─────────────────────────────────────────
   DRAWER UTILITIES
   Drawers are mutually exclusive — opening
   one instantly closes everything else.
───────────────────────────────────────── */
function openDrawer(drawer) {
  closeAllOverlays(drawer);
  drawer.classList.add('is-open');
  drawer.setAttribute('aria-hidden', 'false');
  toggleBackdrop(true);
  lockScroll();
}

function closeDrawer(drawer) {
  drawer.classList.remove('is-open');
  drawer.setAttribute('aria-hidden', 'true');
  toggleBackdrop(false);
  unlockScroll();
}

function toggleBackdrop(show) {
  const backdrop = document.getElementById('Backdrop');
  if (!backdrop) return;
  backdrop.classList.toggle('is-active', show);
}

/* ─────────────────────────────────────────
   BACKDROP CLICK — close any open drawer
───────────────────────────────────────── */
function initBackdrop() {
  document.body.addEventListener('click', (e) => {
    if (e.target.id === 'Backdrop' || e.target.closest('#Backdrop')) {
      // Close slide-in drawers
      document.querySelectorAll('.cart-drawer.is-open, .wishlist-drawer.is-open').forEach(closeDrawer);

      // Close search drawer
      const searchDrawer = document.getElementById('SearchDrawer');
      if (searchDrawer?.classList.contains('is-open')) {
        searchDrawer.classList.remove('is-open');
        searchDrawer.setAttribute('aria-hidden', 'true');
        const searchInput = document.getElementById('SearchInput');
        const searchResults = document.getElementById('SearchResults');
        if (searchInput) searchInput.value = '';
        if (searchResults) searchResults.innerHTML = '';
      }

      // Close mobile menu
      const mobileMenu = document.getElementById('MobileMenu');
      if (mobileMenu?.classList.contains('is-open')) {
        mobileMenu.classList.remove('is-open');
        mobileMenu.setAttribute('aria-hidden', 'true');
        document.getElementById('MobileMenuToggle')?.classList.remove('is-active');
        document.getElementById('MobileMenuToggle')?.setAttribute('aria-expanded', 'false');
      }

      toggleBackdrop(false);
      unlockScroll();
    }
  });
}

/* ─────────────────────────────────────────
   SCROLL REVEAL — IntersectionObserver
   Adds .is-visible to .reveal elements.
   CSS handles the animation.
───────────────────────────────────────── */
function initScrollReveal() {
  if (prefersReducedMotion) return;

  const elements = document.querySelectorAll('.reveal, .reveal-stagger');
  if (!elements.length) return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
  );

  elements.forEach((el) => observer.observe(el));
}

/* ─────────────────────────────────────────
   SIZE SELECTOR
───────────────────────────────────────── */
function initSizeSelector() {
  document.querySelectorAll('.size-selector').forEach((selector) => {
    selector.querySelectorAll('.size-selector__btn:not(.is-unavailable)').forEach((btn) => {
      btn.addEventListener('click', () => {
        selector.querySelectorAll('.size-selector__btn').forEach(b => b.classList.remove('is-active'));
        btn.classList.add('is-active');

        // Update hidden variant input for Shopify
        const variantId = btn.dataset.variantId;
        const variantInput = document.getElementById('SelectedVariantId');
        if (variantInput && variantId) {
          variantInput.value = variantId;
        }
      });
    });
  });
}

/* ─────────────────────────────────────────
   STICKY ADD TO CART — Mobile PDP
   Two-step flow: tap pill → sizes reveal
   inline → tap size → instant AJAX add.
───────────────────────────────────────── */
function initStickyAtc() {
  const inlineCta = document.getElementById('InlineAtc');
  const stickyBar = document.getElementById('StickyAtc');
  if (!inlineCta || !stickyBar) return;

  const observer = new IntersectionObserver(
    ([entry]) => {
      const show = !entry.isIntersecting;
      stickyBar.classList.toggle('is-visible', show);
      stickyBar.setAttribute('aria-hidden', String(!show));
      // Collapse the size picker whenever the bar hides
      if (!show) collapseSizes();
    },
    { threshold: 0, rootMargin: '0px' }
  );

  observer.observe(inlineCta);

  const stickyBtn  = document.getElementById('StickyAtcBtn');
  const sizesRow   = document.getElementById('StickyAtcSizes');
  const form       = document.getElementById('ProductForm');

  function collapseSizes() {
    if (!sizesRow) return;
    sizesRow.setAttribute('hidden', '');
    stickyBar.classList.remove('is-expanded');
  }

  function submitForm() {
    if (form) form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
  }

  if (stickyBtn) {
    stickyBtn.addEventListener('click', () => {
      // No size options on this product → add straight away
      if (!sizesRow) { submitForm(); return; }

      // First tap: reveal sizes inline. Second tap: collapse.
      if (sizesRow.hasAttribute('hidden')) {
        sizesRow.removeAttribute('hidden');
        stickyBar.classList.add('is-expanded');
      } else {
        collapseSizes();
      }
    });
  }

  if (sizesRow) {
    sizesRow.addEventListener('click', (e) => {
      const sizeBtn = e.target.closest('[data-sticky-variant]');
      if (!sizeBtn || sizeBtn.disabled) return;

      // Sync with the main size selector (updates variant input + ATC state)
      const mainBtn = document.querySelector(
        `.size-selector__btn[data-variant-id="${sizeBtn.dataset.stickyVariant}"]`
      );
      if (mainBtn) {
        mainBtn.click();
      } else {
        const variantInput = document.getElementById('SelectedVariantId');
        if (variantInput) variantInput.value = sizeBtn.dataset.stickyVariant;
      }

      collapseSizes();
      submitForm();  // AJAX add — instant, no page reload
    });
  }
}
/* ─────────────────────────────────────────
   SEARCH EXCLUSIVITY — capture phase runs
   BEFORE the search drawer's own open
   handler, so other overlays snap shut first.
───────────────────────────────────────── */
function initSearchExclusivity() {
  document.body.addEventListener('click', (e) => {
    if (e.target.closest('[data-search-open]')) {
      closeAllOverlays(document.getElementById('SearchDrawer'));
    }
  }, true);
}

/* ─────────────────────────────────────────
   FOOTER REVEAL — GLOBAL curtain on every template. The footer sits fixed under
   the page and content scrolls up to reveal it. JS measures the footer height,
   exposes it as --footer-height, and toggles `has-footer-reveal` (base.css does
   the pinning + margin). No scroll listeners — zero jank. Recomputed on load,
   resize, orientation change, and any footer size change (ResizeObserver).
───────────────────────────────────────── */
function initFooterReveal() {
  const footer = document.querySelector('.site-footer');
  if (!footer) return;

  function setHeight() {
    const h = footer.offsetHeight;
    // Only pin the footer if it is SHORTER than the viewport. A footer taller
    // than the screen (e.g. a small phone with many stacked links) can't be
    // pinned — its top would sit permanently off-screen — so fall back to
    // normal document flow (no clipping, no overlap).
    const fits = h > 0 && h < window.innerHeight;

    if (fits) {
      document.documentElement.style.setProperty('--footer-height', h + 'px');
    } else {
      document.documentElement.style.removeProperty('--footer-height');
    }
    document.body.classList.toggle('has-footer-reveal', fits);
  }

  setHeight();
  // Re-measure once everything (fonts/images) has settled.
  window.addEventListener('load', setHeight);
  window.addEventListener('orientationchange', setHeight);

  if ('ResizeObserver' in window) {
    // Fires on footer height changes AND on viewport resizes that reflow it.
    new ResizeObserver(setHeight).observe(footer);
  } else {
    window.addEventListener('resize', setHeight, { passive: true });
  }
}

/* ─────────────────────────────────────────
   VIDEO KEEP-ALIVE
   Guarantees every autoplay video loops
   forever: enforces attributes and force-
   plays whenever the video is in view,
   regardless of scroll speed/direction.
───────────────────────────────────────── */
function initVideoKeepAlive() {
  const videos = document.querySelectorAll('video');
  if (!videos.length) return;
  // iPhone Low Power Mode: autoplay is blocked until the visitor touches the
  // page. On the first tap/touch anywhere, start every video.
  const kvStartAll = () => {
    document.querySelectorAll('video').forEach(v => { v.muted = true; v.play().catch(() => {}); });
    ['touchend', 'click'].forEach(ev => document.removeEventListener(ev, kvStartAll, true));
  };
  ['touchend', 'click'].forEach(ev => document.addEventListener(ev, kvStartAll, { capture: true, passive: true }));
  videos.forEach(v => {
    // Properties AND attributes — some browsers honour one but not the other.
    // muted + playsinline are MANDATORY for autoplay to be allowed (esp. iOS).
    v.muted = true;
    v.defaultMuted = true;
    v.loop = true;
    v.playsInline = true;
    v.setAttribute('muted', '');
    v.setAttribute('loop', '');
    v.setAttribute('playsinline', '');
    v.setAttribute('webkit-playsinline', ''); // older iOS Safari
    v.setAttribute('autoplay', '');
    // If the browser power-saver / tab-blur pauses it while visible, restart it.
    v.addEventListener('pause', () => {
      if (v.dataset.inView === 'true') v.play().catch(() => {});
    });
    // Loop insurance — if the `loop` attribute is ever stripped, restart on end
    // so playback stays continuous with no visible stop.
    v.addEventListener('ended', () => {
      v.currentTime = 0;
      v.play().catch(() => {});
    });
  });

  if (!('IntersectionObserver' in window)) return;

  const io = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      const v = entry.target;
      v.dataset.inView = entry.isIntersecting ? 'true' : 'false';
      if (entry.isIntersecting) v.play().catch(() => {});
    });
  }, { threshold: 0.05 });

    videos.forEach(v => io.observe(v));

  /* bfcache fix: mobile browsers restore cached pages with videos PAUSED
     (showing the native play button). Force-restart every video on any
     page restore or refresh. */
  window.addEventListener('pageshow', (e) => {
    document.querySelectorAll('video').forEach(v => {
      v.muted = true;
      v.play().catch(() => {});
    });

    // Force unlock scroll and close all overlays/drawers on page show/restore from cache (bfcache)
    _scrollLocked = false;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.overflow = '';

    if (typeof closeAllOverlays === 'function') {
      closeAllOverlays();
    } else if (window.KVORE && window.KVORE.closeAllOverlays) {
      window.KVORE.closeAllOverlays();
    }

    const backdrop = document.getElementById('Backdrop');
    if (backdrop) backdrop.classList.remove('is-active');

    if (window.KVORE && window.KVORE.resetScrollLockY) {
      window.KVORE.resetScrollLockY();
    }

    /* CACHING FIX — a bfcache restore (back/forward) shows the page's ORIGINAL
       server-rendered cart badge, which can be stale if the cart changed since.
       Reconcile the cart count from source (/cart.js) and the wishlist count
       from localStorage. Gated on `persisted` so a NORMAL load — where the
       server-rendered count is already fresh — costs zero extra requests
       (important at scale). */
    if (e && e.persisted) {
      fetch('/cart.js')
        .then(r => r.json())
        .then(cart => updateCartCount(cart.item_count))
        .catch(() => {});
      if (window.KVORE && window.KVORE.Wishlist) window.KVORE.Wishlist.syncCount();
    }
  });
}

/* ─────────────────────────────────────────
   WISHLIST — localStorage + metafield
───────────────────────────────────────── */
const Wishlist = {
  key: 'kvore_wishlist',

  // Storage format: [{id: '123', handle: 'product-handle'}, ...]
  get() {
    try {
      const raw = JSON.parse(localStorage.getItem(this.key)) || [];
      // Migrate legacy format (plain id array) — drop entries without handle
      // since we can't fetch them without it.
      return raw.filter(entry => entry && typeof entry === 'object' && entry.handle);
    } catch {
      return [];
    }
  },

  add(productId, handle) {
    productId = String(productId);
    const list = this.get();
    if (!list.find(e => String(e.id) === productId)) {
      list.push({ id: productId, handle });
      localStorage.setItem(this.key, JSON.stringify(list));
    }
    this.updateUI(productId, true);
    this.syncCount();
    this.showToast();
  },

  showToast() {
    let toast = document.getElementById('KVOREMoodboardToast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'KVOREMoodboardToast';
      toast.setAttribute('aria-live', 'polite');
      toast.style.cssText = `
        position:fixed; bottom:24px; left:50%; transform:translateX(-50%) translateY(12px);
        background:rgba(9,9,11,0.88); backdrop-filter:blur(16px); -webkit-backdrop-filter:blur(16px);
        border:1px solid rgba(255,255,255,0.08); border-radius:40px;
        padding:10px 20px; z-index:999999; opacity:0;
        transition:opacity 0.25s ease, transform 0.25s ease;
        font-family:var(--f-sans); font-size:11px; font-weight:500;
        letter-spacing:0.1em; text-transform:uppercase; white-space:nowrap;
        color:var(--success,#3d8f5f); pointer-events:none;
      `;
      toast.textContent = '+ Added to Moodboard';
      document.body.appendChild(toast);
    }
    clearTimeout(toast._hideTimer);
    requestAnimationFrame(() => {
      toast.style.opacity = '1';
      toast.style.transform = 'translateX(-50%) translateY(0)';
    });
    toast._hideTimer = setTimeout(() => {
      toast.style.opacity = '0';
      toast.style.transform = 'translateX(-50%) translateY(12px)';
    }, 2200);
  },

  remove(productId) {
    productId = String(productId);
    const list = this.get().filter(e => String(e.id) !== productId);
    localStorage.setItem(this.key, JSON.stringify(list));
    this.updateUI(productId, false);
    this.syncCount();
  },

  toggle(productId, handle) {
    this.has(productId) ? this.remove(productId) : this.add(productId, handle);
  },

  has(productId) {
    productId = String(productId);
    return !!this.get().find(e => String(e.id) === productId);
  },

  syncCount() {
    const count = this.get().length;
    document.querySelectorAll('[data-wishlist-count]').forEach(el => {
      el.textContent = count;
      el.style.display = count > 0 ? '' : 'none';
    });
  },

  updateUI(productId, isActive) {
    document.querySelectorAll(`[data-wishlist-toggle="${productId}"]`).forEach(btn => {
      btn.classList.toggle('is-active', isActive);
      btn.setAttribute('aria-label', isActive ? 'Remove from wishlist' : 'Add to wishlist');
    });
  },

  init() {
    document.querySelectorAll('[data-wishlist-toggle]').forEach(btn => {
      const id     = btn.dataset.wishlistToggle;
      const handle = btn.dataset.wishlistHandle || '';
      this.updateUI(id, this.has(id));
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.toggle(id, handle);
      });
    });
    this.syncCount();
  }
};

window.KVORE.Wishlist = Wishlist;

/* ─────────────────────────────────────────
   COUNTDOWN TIMER
───────────────────────────────────────── */
function initCountdowns() {
  document.querySelectorAll('[data-countdown]').forEach((el) => {
    const launchDate = new Date(el.dataset.countdown);
    if (isNaN(launchDate)) return;

    function update() {
      const now = new Date();
      const diff = launchDate - now;

      if (diff <= 0) {
        el.closest('[data-countdown-wrap]')?.remove();
        return;
      }

      const days    = Math.floor(diff / 86400000);
      const hours   = Math.floor((diff % 86400000) / 3600000);
      const minutes = Math.floor((diff % 3600000) / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);

      el.querySelector('[data-countdown-days]').textContent    = String(days).padStart(2, '0');
      el.querySelector('[data-countdown-hours]').textContent   = String(hours).padStart(2, '0');
      el.querySelector('[data-countdown-minutes]').textContent = String(minutes).padStart(2, '0');
      el.querySelector('[data-countdown-seconds]').textContent = String(seconds).padStart(2, '0');
    }

    update();
    setInterval(update, 1000);
  });
}

/* ─────────────────────────────────────────
   ADD TO CART — AJAX intercept
   One request: the add response carries the
   re-rendered cart drawer via the Section
   Rendering API. Falls back to native submit.
─────────────────────────────────────────── */
function initAddToCart() {
  const form = document.getElementById('ProductForm');
  if (!form) return;

  const atcBtn = document.getElementById('AddToCartBtn');

  form.addEventListener('submit', (e) => {
    e.preventDefault();

    const formData = new FormData(form);
    // Ask Shopify to return the re-rendered drawer + cart JSON in ONE response
    formData.append('sections', 'cart-drawer');
    const strings = window.KVORE?.strings || {};
    if (atcBtn) {
      atcBtn.disabled = true;
      atcBtn.textContent = strings.adding || 'Adding…';
    }

    /* Do NOT open the drawer yet — renderCartAndOpen() opens it only AFTER the
       item is rendered and the badge updated, so no empty/loading drawer. */
    fetch('/cart/add.js', {
      method: 'POST',
      body: formData,
    })
      .then(r => {
        if (!r.ok) throw new Error('add failed');
        return r.json();
      })
      .then(item => {
        // Render drawer (from the piggybacked section) + update badge, THEN open.
        renderCartAndOpen(item);

        // Success flash — green "Added ✓" for 1.8s
        if (atcBtn) {
          atcBtn.disabled = false;
          atcBtn.classList.add('is-success');
          atcBtn.textContent = window.KVORE?.strings?.added || 'Added ✓';
          setTimeout(() => {
            atcBtn.classList.remove('is-success');
            atcBtn.textContent = window.KVORE?.strings?.addToCart || 'Add to Cart';
          }, 1800);
        }
      })
      .catch(() => {
        form.submit();
      })
      .finally(() => {
        // Only reset if success flash isn't running
        if (atcBtn && !atcBtn.classList.contains('is-success')) {
          atcBtn.disabled = false;
          atcBtn.textContent = window.KVORE?.strings?.addToCart || 'Add to Cart';
        }
      });
  });
}

function initAnnouncementBar() {
  const bar = document.getElementById('AnnouncementBar');
  if (!bar) return;

  const items = bar.querySelectorAll('[data-announcement-item]');
  if (items.length <= 1) return;

  let current = 0;

  function next() {
    items[current].classList.remove('is-active');
    current = (current + 1) % items.length;
    items[current].classList.add('is-active');
  }

  items[0].classList.add('is-active');
  setInterval(next, 4000);
}

/* ─────────────────────────────────────────
   SIZE CHART MODAL
───────────────────────────────────────── */
function initSizeChart() {
  const modal    = document.getElementById('SizeChartModal');
  const openBtn  = document.getElementById('SizeChartOpen');
  const closeBtn = document.getElementById('SizeChartClose');
  if (!modal || !openBtn) return;

  function openModal() {
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    lockScroll();
  }

  function closeModal() {
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    unlockScroll();
  }

  openBtn.addEventListener('click', openModal);
  if (closeBtn) closeBtn.addEventListener('click', closeModal);

  // Close on backdrop click (outside panel)
  modal.addEventListener('click', (e) => {
    if (e.target === modal) closeModal();
  });

  // Close on ESC
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && modal.classList.contains('is-open')) closeModal();
  });
}

/* ─────────────────────────────────────────
   HOME / LOGO — return to the hero
   The homepage scrolls continuously (no scroll-snap). This wires the Home/logo
   action to glide reliably back to the hero from anywhere, and forces the page
   to always LOAD at the hero (never a restored mid-page position).
───────────────────────────────────────── */
function initFullpageScroll() {
  if (!document.body.classList.contains("template-index")) return;

  /* A reload / back-forward must always land on the hero, never a browser-
     restored mid-page position. (The actual scroll-to-0 on load is done by the
     pageshow reset in theme.liquid's <head>.) */
  if ('scrollRestoration' in history) {
    history.scrollRestoration = 'manual';
  }

  /* goHome / scrollToTop — reliable, smooth return to the hero from ANYWHERE on
     the homepage. Closes any overlay, HARD-releases the iOS scroll lock (without
     restoring the cached position), clears the backdrop, then smooth-scrolls to
     the very top. No scroll-snap juggling (snap was removed) — nothing can
     strand the scroll on an intermediate section, so it ALWAYS lands on the hero. */
  function goHome() {
    if (typeof closeAllOverlays === "function") closeAllOverlays();

    document.body.style.position = "";
    document.body.style.top = "";
    document.body.style.left = "";
    document.body.style.right = "";
    document.body.style.overflow = "";
    _scrollLocked = false;
    _scrollLockY = 0;

    const backdrop = document.getElementById("Backdrop");
    if (backdrop) backdrop.classList.remove("is-active");

    /* Already at the hero → do nothing. Re-firing a scroll here (esp. on mobile,
       where clearing the fixed-body lock has just settled the viewport) could
       nudge the hero and read as an unexpected shift/scroll-down. Only glide when
       we're genuinely scrolled away from the top. */
    if (window.scrollY <= 1) return;

    window.scrollTo({ top: 0, left: 0, behavior: prefersReducedMotion ? "auto" : "smooth" });
  }

  window.KVORE.goHome = goHome;
  window.KVORE.scrollToTop = goHome;   // legacy alias

  /* Wire the header + footer logo to goHome ON THE HOMEPAGE only. We're already
     on "/", so a plain <a href="/"> would trigger a same-URL reload that lands
     wherever the browser restored (glitchy, and inconsistent on mobile). Instead
     we intercept and smooth-glide to the hero — one tap, always top, no flash,
     identical on desktop and mobile. On every OTHER template the logo keeps its
     native <a href="/"> behaviour, so it navigates home and loads at the top. */
  const homeLinks = document.querySelectorAll('.site-nav__logo, .site-footer__logo');
  homeLinks.forEach((link) => {
    link.addEventListener('click', (e) => {
      // Respect new-tab / modified clicks — let the browser do its thing.
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      e.preventDefault();
      window.KVORE.goHome();
    });
  });
}

/* Footer navigation is now NATIVE — no JS interception, no page fade-out.
   The old initFooterNavTransition() faded <body> to opacity:0 and delayed the
   navigation up to 450ms, which read as a black/blank screen before the next
   page appeared. Footer links are plain <a href> that navigate instantly; the
   only remaining feedback is the CSS :active press state (base.css). */

document.addEventListener('DOMContentLoaded', () => {
  initNav();
  initMobileMenu();
  initCartDrawer();
  initCartControls();
  initAddToCart();
  initWishlistDrawer();
  initBackdrop();
  initScrollReveal();
  initSizeSelector();
  initStickyAtc();
  initCountdowns();
  initAnnouncementBar();
    initSizeChart();
  initVideoKeepAlive();
  initSearchExclusivity();
  initFooterReveal();
  initFullpageScroll();
  Wishlist.init();
});